import { useSelector } from 'react-redux'
import { useQuery } from '@tanstack/react-query'

import { QuickLinkCard } from './QuickLinkCard'

import styles from './styles.module.scss'

import { getWordsDictated } from '@/api/teamInsights'
import { getEnterpriseMembersCount } from '@/api/teamMembersTable'
import { useAnalytics } from '@/hooks/useAnalytics'
import { useHasDepartments } from '@/hooks/useHasDepartments'
import { selectCurrentEnterprise, selectIsAdmin, selectIsSalesOwned } from '@/store/features/user/userSlice'
import { EventName } from '@/utils/eventDefinitions'

export const QuickLinks = () => {
  const isAdmin = useSelector(selectIsAdmin)
  const isSalesOwned = useSelector(selectIsSalesOwned)
  const enterprise = useSelector(selectCurrentEnterprise)
  const enterpriseId = enterprise?.id
  const hasDepts = useHasDepartments()
  const { trackEvent } = useAnalytics()

  const membersCount = useQuery({
    queryKey: ['team-members', 'enterprise-count', enterpriseId],
    queryFn: () => getEnterpriseMembersCount().then((r) => r.data),
    enabled: !!enterpriseId,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  // No date filters => backend returns the lifetime total. Admin-only endpoint
  // (403 for members), so this is a UX gate on isAdmin, not a security boundary.
  // 'lifetime' in the params slot mirrors the sibling [..., enterpriseId, params]
  // shape so a prefix invalidation on [team-insights, words-dictated, enterpriseId]
  // still catches this entry.
  const words = useQuery({
    queryKey: ['team-insights', 'words-dictated', enterpriseId, 'lifetime'],
    queryFn: () => getWordsDictated(enterpriseId!).then((r) => r.data.total),
    enabled: isAdmin && !!enterpriseId,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  const orgOrTeamLabel = enterprise?.name
    ? hasDepts
      ? enterprise.name
      : `${enterprise.name}'s team`
    : hasDepts
      ? 'your organization'
      : 'your team'
  const count = membersCount.data?.total

  const membersSubtitle =
    count === undefined
      ? 'View and manage your team'
      : `${count.toLocaleString()} ${count === 1 ? 'member' : 'members'} ${hasDepts ? 'in' : 'on'} ${orgOrTeamLabel}`

  const usageSubtitle =
    words.data === undefined ? 'See how your team is using Flow' : `${words.data.toLocaleString()} words total`

  const track = (target: 'team_members' | 'usage' | 'billing') => () =>
    trackEvent?.(EventName.HomeCtaClicked, { target })

  return (
    <div className={styles.row}>
      <QuickLinkCard
        title={hasDepts ? 'Organization Members' : 'Team Members'}
        subtitle={membersSubtitle}
        ctaLabel="Go to members"
        to="/app/team"
        onClick={track('team_members')}
      />
      {isAdmin && (
        <QuickLinkCard
          title="Usage"
          subtitle={usageSubtitle}
          ctaLabel="Go to usage"
          to="/app/usage"
          onClick={track('usage')}
        />
      )}
      {isAdmin && !isSalesOwned && (
        <QuickLinkCard
          title="Billing"
          subtitle="Manage subscription & payments"
          ctaLabel="Go to billing"
          to="/app/settings/billing"
          onClick={track('billing')}
        />
      )}
    </div>
  )
}
